import type { AiReportStatus, AiReportType } from '@erp/shared';
import { BarChart3, Boxes, HelpCircle, Landmark, type LucideIcon, ScrollText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/shadcn/index.js';

export const TYPE_ICON: Record<AiReportType, LucideIcon> = {
  business_summary: ScrollText,
  sales_performance: BarChart3,
  inventory_health: Boxes,
  finance_overview: Landmark,
  custom_question: HelpCircle,
};

/** Status chip: generating pulses, ready is green, failed is red. */
export function StatusBadge({ status }: { status: AiReportStatus }): JSX.Element {
  const { t } = useTranslation('aiReports');
  if (status === 'running') {
    return (
      <Badge variant="warning">
        <span className="relative flex h-2 w-2" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-warning" />
        </span>
        {t('aiReports.status.running', { defaultValue: 'Generating…' })}
      </Badge>
    );
  }
  if (status === 'failed') {
    return <Badge variant="danger">{t('aiReports.status.failed', { defaultValue: 'Failed' })}</Badge>;
  }
  return <Badge variant="success">{t('aiReports.status.succeeded', { defaultValue: 'Ready' })}</Badge>;
}

/** The failed row's human sentence for a machine error_code. */
export function useErrorText(): (code: string | null) => string {
  const { t } = useTranslation('aiReports');
  return (code) => {
    const known = ['ai_not_configured', 'ai_timeout', 'ai_provider_error', 'ai_bad_output', 'stale'];
    const key = known.includes(code ?? '') ? code : 'generic';
    return t(`aiReports.error.${key}`, { defaultValue: t('aiReports.error.generic', { defaultValue: 'Something went wrong while generating this report.' }) });
  };
}
